import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ContactFormDialog from "@/components/customers/ContactFormDialog";
import SectionCard from "@/components/customers/SectionCard";
import {
  useAnonymizeContact,
  useConfirmAgentContact,
  useCustomerContacts,
  useSetContactInactive,
  useSetPrimaryContact,
} from "@/hooks/use-customer-contacts";
import type { DBCustomerContact } from "@/lib/db-types";

const SOURCE_LABELS: Partial<Record<DBCustomerContact["source"], string>> = {
  po_mirror: "Fra PowerOffice",
  brreg_role: "Fra Brønnøysund",
  linkedin: "Fra LinkedIn",
};

function ContactRow({
  contact,
  onEdit,
  onDelete,
}: {
  contact: DBCustomerContact;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const setPrimary = useSetPrimaryContact();
  const setInactive = useSetContactInactive();
  const confirm = useConfirmAgentContact();
  const gone = !!contact.anonymized_at;
  const needsConfirm = contact.suggested_by_agent && !contact.confirmed_at && !gone;
  const sourceLabel = SOURCE_LABELS[contact.source];

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm">
      <div className="min-w-0 flex-1">
        <div className={gone || !contact.is_active ? "font-medium text-muted-foreground" : "font-medium"}>
          {contact.full_name}
          {contact.title && <span className="font-normal text-muted-foreground"> · {contact.title}</span>}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {[contact.email, contact.phone].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {contact.is_primary && <Badge variant="secondary">Hovedkontakt</Badge>}
        {!contact.is_active && !gone && <Badge variant="outline">Inaktiv</Badge>}
        {sourceLabel && <Badge variant="outline">{sourceLabel}</Badge>}
        {needsConfirm && <Badge variant="outline">Foreslått av agent</Badge>}
        {needsConfirm && (
          <Button size="sm" variant="outline" className="h-7 text-xs" aria-label={`Bekreft ${contact.full_name}`} onClick={() => confirm.mutate(contact.id)}>
            Bekreft
          </Button>
        )}
      </div>
      {!gone && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Meny for ${contact.full_name}`}>
              <SrIcon name="more-horizontal" className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>Rediger</DropdownMenuItem>
            {contact.is_active && !contact.is_primary && (
              <DropdownMenuItem onSelect={() => setPrimary.mutate(contact)}>Sett som hovedkontakt</DropdownMenuItem>
            )}
            {contact.is_active && (
              <DropdownMenuItem onSelect={() => setInactive.mutate({ id: contact.id })}>Sett inaktiv</DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onDelete} className="text-destructive">
              Slett person
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}

/** Kontaktpersoner på kundekortet. «Slett person» anonymiserer (GDPR), raden beholdes. Uten kontakter er kortet én linje. */
export default function CustomerContactsSection({ customerId }: { customerId: string }) {
  const { data: contacts = [], isLoading } = useCustomerContacts(customerId);
  const anonymize = useAnonymizeContact();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DBCustomerContact | null>(null);
  const [deleting, setDeleting] = useState<DBCustomerContact | null>(null);

  const visible = contacts.filter((c) => !c.anonymized_at).length;

  return (
    <>
      <SectionCard
        compact
        title="Kontaktpersoner"
        meta={visible > 0 ? String(visible) : undefined}
        empty="Ingen ennå. Legg til en for å sende e-post og ringe fra kortet."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <SrIcon name="plus" className="mr-1 h-3 w-3" /> Ny kontaktperson
          </Button>
        }
      >
        {isLoading ? (
          <Skeleton className="m-4 h-16" />
        ) : contacts.length === 0 ? null : (
          <ul aria-label="Kontaktpersoner" className="divide-y divide-border px-4">
            {contacts.map((c) => (
              <ContactRow
                key={c.id}
                contact={c}
                onEdit={() => {
                  setEditing(c);
                  setFormOpen(true);
                }}
                onDelete={() => setDeleting(c)}
              />
            ))}
          </ul>
        )}
      </SectionCard>

      <ContactFormDialog open={formOpen} onOpenChange={setFormOpen} customerId={customerId} contact={editing} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette {deleting?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Navn, e-post, mobil, tittel og notater fjernes for godt. Samtaler og e-post i kontaktloggen beholdes, men
              peker da til «Slettet person». Dette kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting) anonymize.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Slett person
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
